import React, { useState, useCallback, useRef } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  Autocomplete,
  Typography,
  CircularProgress,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Search, Building2 } from 'lucide-react'
import {
  searchLegalNotices,
  autocompleteLegalNotices,
  setEntreprisesQuery,
  clearEntreprisesFilters,
} from '../../../redux/actions/entreprisesAction'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const EntreprisesSearchBar = ({ compact = false }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const dk = useSearchTokens()

  const query = useSelector((state) => state.entreprises.query)
  const autocompleteResults = useSelector((state) => state.entreprises.autocompleteResults)
  const isLoading = useSelector((state) => state.entreprises.loading.autocomplete)

  const [inputValue, setInputValue] = useState(query || '')
  const debounceRef = useRef(null)

  // Sync inputValue when query is cleared by reducer (e.g. applied_filters)
  const prevQueryRef = useRef(query)
  if (prevQueryRef.current !== query) {
    prevQueryRef.current = query
    if (query === '' && inputValue !== '') {
      setInputValue('')
    }
  }

  const handleInputChange = useCallback((event, newValue, reason) => {
    if (reason === 'reset') return
    setInputValue(newValue)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (newValue.length >= 2) {
      debounceRef.current = setTimeout(() => {
        dispatch(autocompleteLegalNotices(newValue))
      }, 300)
    }
  }, [dispatch])

  const handleSearch = useCallback((searchQuery) => {
    const q = searchQuery?.trim() ?? ''
    dispatch(setEntreprisesQuery(q))
    // Clear existing filters — backend will re-apply via applied_filters if query matches an enum
    dispatch(clearEntreprisesFilters())
    dispatch(searchLegalNotices({ q, page: 1, filters: {} }))
  }, [dispatch])

  const handleSubmit = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSearch(inputValue)
    }
  }, [inputValue, handleSearch])

  const handleOptionSelect = useCallback((event, option) => {
    if (!option) return
    if (typeof option === 'string') {
      handleSearch(option)
    } else {
      const q = option.denomination || option.registre_commerce || ''
      setInputValue(q)
      handleSearch(q)
    }
  }, [handleSearch])

  const options = Array.isArray(autocompleteResults) ? autocompleteResults : []

  return (
    <Autocomplete
      data-tour="entreprises-search"
      freeSolo
      options={options}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.denomination || ''
      }
      filterOptions={(x) => x}
      loading={isLoading}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            '&:hover': { backgroundColor: dk.hover },
            cursor: 'pointer',
          }}
        >
          <Building2 size={14} color={dk.text3} />
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              sx={{
                fontSize: '0.82rem',
                fontWeight: 600,
                color: dk.text1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.denomination}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {option.registre_commerce && (
                <Typography sx={{ fontSize: '0.7rem', color: dk.text3, fontWeight: 500 }}>
                  RC: {option.registre_commerce}
                </Typography>
              )}
              {option.ville && (
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    color: dk.text3,
                    backgroundColor: dk.chipBg,
                    px: 0.6,
                    py: 0.1,
                    borderRadius: '3px',
                  }}
                >
                  {option.ville}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('entreprises_search_placeholder')}
          variant="outlined"
          onKeyDown={handleSubmit}
          size={compact ? 'small' : 'medium'}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Search size={compact ? 16 : 18} color={dk.gold} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} sx={{ color: dk.gold }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: dk.inputBg,
              borderRadius: compact ? '8px' : '12px',
              fontSize: compact ? '0.85rem' : '0.95rem',
              '& fieldset': {
                borderColor: dk.inputBorder,
              },
              '&:hover fieldset': {
                borderColor: dk.goldBdr,
              },
              '&.Mui-focused fieldset': {
                borderColor: dk.gold,
                borderWidth: '1px',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: dk.text3,
              opacity: 1,
            },
          }}
        />
      )}
      componentsProps={{
        paper: {
          sx: {
            backgroundColor: dk.elevated,
            border: `1px solid ${dk.border}`,
            borderRadius: '10px',
            boxShadow: dk.shadowHover,
            mt: 0.5,
          },
        },
      }}
    />
  )
}

export default EntreprisesSearchBar
